import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import EnhancedPurchasePlanButton from '@/components/EnhancedPurchasePlanButton';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getPlanById } from '@/config/pricingPlans';
import { updateOrderWithUserEmail } from '@/services/profileService';
import { Link } from 'react-router-dom';
import { Mail, ShieldCheck } from 'lucide-react';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [validForm, setValidForm] = useState(false);
  
  const searchParams = new URLSearchParams(location.search);
  const selectedPlan = searchParams.get('plan') || 'basic';
  
  useEffect(() => {
    if (selectedPlan) {
      sessionStorage.setItem('selected_plan', selectedPlan);
    }
  }, [selectedPlan]);
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderStatus = searchParams.get('order_status');
    const orderId = searchParams.get('order_id');
    
    if (orderId && orderStatus) {
      if (orderStatus === 'SUCCESS' || orderStatus === 'PAID') {
        sessionStorage.setItem('last_order_id', orderId);
        if (email) sessionStorage.setItem('customer_email', email);
        
        const selectedPlan = searchParams.get('plan') || sessionStorage.getItem('selected_plan') || 'basic';
        sessionStorage.setItem('purchased_plan', selectedPlan);
        
        updateOrderStatus(orderId, orderStatus, email);
        
        toast({
          title: "Payment Successful",
          description: "Your subscription has been activated. Redirecting to thank you page.",
        });
        
        const thankYouUrl = `/thank-you?order_id=${orderId}&plan=${selectedPlan}`;
        
        setTimeout(() => {
          navigate(thankYouUrl);
        }, 1500);
      } else {
        toast({
          title: "Payment Failed",
          description: `Payment status: ${orderStatus}. Please try again.`,
          variant: "destructive"
        });
      }
    }
  }, [location.search, navigate, toast, email]);
  
  const updateOrderStatus = async (orderId: string, status: string, email: string) => {
    try {
      console.log(`CHECKOUT: Updating payment status for order ${orderId} to ${status} for email ${email}`);
      
      const { error } = await supabase
        .from('payment_orders')
        .update({ status })
        .eq('order_id', orderId);
      
      if (error) {
        console.error('CHECKOUT: Error updating payment status:', error);
      } else {
        console.log('CHECKOUT: Payment status updated successfully');
        
        if (email) {
          const emailUpdateResult = await updateOrderWithUserEmail(orderId, email);
          console.log(`CHECKOUT: Order email update result: ${emailUpdateResult ? 'Success' : 'Failed'}`);
        }
      }
    } catch (error) {
      console.error('CHECKOUT: Exception updating payment status:', error);
    }
  };
  
  const currentPlan = getPlanById(selectedPlan);

  const validateForm = () => {
    if (!email || !name || !phone) {
      toast({
        title: "Missing information",
        description: "Please provide name, email, and phone number to continue.",
        variant: "destructive"
      });
      setValidForm(false);
      return false;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please provide a valid email address.",
        variant: "destructive"
      });
      setValidForm(false);
      return false;
    }
    
    if (!/^[6-9]\d{9}$/.test(phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please provide a valid 10-digit Indian phone number starting with 6, 7, 8, or 9.",
        variant: "destructive"
      });
      setValidForm(false);
      return false;
    }
    
    setValidForm(true);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
    }
  };

  React.useEffect(() => {
    const isEmailValid = email && /\S+@\S+\.\S+/.test(email);
    const isPhoneValid = phone && /^[6-9]\d{9}$/.test(phone);
    
    if (name && isEmailValid && isPhoneValid) {
      setValidForm(true);
    } else {
      setValidForm(false);
    }
  }, [name, email, phone]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-krix-purple/5 to-transparent p-4">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Complete Your Order</CardTitle>
          <CardDescription className="text-center">
            You're purchasing {currentPlan.title} Plan at {currentPlan.price.replace('/mon', '')}
          </CardDescription>
          <div className="flex items-center justify-center gap-2 mt-2 text-green-600">
            <ShieldCheck className="h-5 w-5" />
            <p className="text-sm font-medium">
              7-day money-back guarantee included
            </p>
          </div>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
              <Input 
                id="name" 
                placeholder="Your full name" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="you@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
              <Input 
                id="phone" 
                type="tel" 
                placeholder="10-digit phone number" 
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={10}
                required
              />
            </div>
            
            <div className="border rounded-md p-4 space-y-2">
              <h3 className="font-medium">{currentPlan.title} Plan includes:</h3>
              <ul className="space-y-1">
                {currentPlan.features.map((feature, index) => (
                  feature.included && (
                    <li key={index} className="text-sm flex items-center">
                      <span className="text-krix-purple mr-2">✓</span> {feature.name}
                    </li>
                  )
                ))}
              </ul>
              <div className="text-right font-bold pt-2">
                {currentPlan.price}
              </div>
            </div>
            
            <EnhancedPurchasePlanButton 
              planId={selectedPlan}
              variant="cta"
              customerName={validForm ? name : undefined}
              customerEmail={validForm ? email : undefined}
              customerPhone={phone || undefined}
            />
            
            <div className="rounded-md p-3 bg-blue-50 border border-blue-100">
              <div className="flex items-start">
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Your data is secure</h4>
                  <p className="text-xs text-blue-700">We use industry-standard encryption to protect your personal and payment information. We never store your full payment details on our servers.</p>
                </div>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center">
                  <img src="https://logowik.com/content/uploads/images/cashfree-payments7934.logowik.com.webp" alt="Cashfree Payments" className="h-8 mr-2" />
                  <img src="https://cashfreelogo.cashfree.com/assets_images/marketing/pcidss.svg" alt="PCI Compliant" className="h-6" />
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center space-y-3 pt-2 border-t">
              <div className="flex items-center">
                <Mail className="h-4 w-4 text-blue-600 mr-1.5" />
                <span className="text-xs">Need help? Contact <a href="mailto:support@krix.app" className="text-primary hover:underline">support@krix.app</a></span>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-xs text-center space-y-2 mt-2">
              <div className="flex justify-center space-x-2 mt-2">
                <img src="https://img.icons8.com/color/48/000000/visa.png" alt="Visa" className="h-6" />
                <img src="https://img.icons8.com/color/48/000000/mastercard.png" alt="Mastercard" className="h-6" />
                <img src="https://img.icons8.com/color/48/000000/amex.png" alt="American Express" className="h-6" />
                <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTRXMWQZbSt4akXW3w0WEJXAK-loZHrJ1JEjQ&s" alt="Google Pay" className="h-6" />
                <img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ5IlNqsyTLlEQwaH3Hkps-usveIIh8-FrX__Kn4tA7KaKIzuAG9jphELYyZpL3-CsSfA&usqp=CAU" alt="PhonePe" className="h-6" />
                <img src="https://img.icons8.com/color/48/000000/rupay.png" alt="RuPay" className="h-6" />
              </div>
              
              <p className="text-muted-foreground">
                By completing your purchase, you agree to our <Link to="/terms" className="text-primary font-medium hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-primary font-medium hover:underline">Privacy Policy</Link>.
              </p>
              <p className="text-muted-foreground">
                Unsatisfied? View our <Link to="/refund" className="text-primary font-medium hover:underline">Refund Policy</Link>. You can cancel your subscription at any time.
              </p>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CheckoutPage;
